import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreativeImportIssueDto, CreativeImportPreviewDto } from "@nexus/contracts";
import { Button, cn } from "@nexus/ui";
import { AlertCircle, Check, ChevronRight, Circle, FileSpreadsheet } from "lucide-react";
import type { ImportReadiness } from "./import-readiness";
import {
  deriveImportSummaryPhase,
  progressStepNumber,
  progressStepState,
  type ImportSummaryPhase,
  type ProgressStepId,
} from "./import-summary-next-step";
import { ImportIssuesDialog } from "./ImportIssuesDialog";

interface ImportSummaryPanelProps {
  preview: CreativeImportPreviewDto;
  readiness: ImportReadiness;
  blockingIssues: CreativeImportIssueDto[];
  warningIssues: CreativeImportIssueDto[];
  hasVendorMapping: boolean;
  isCommitting: boolean;
  onOpenVendorMapping: () => void;
  onAcceptPrefilledDates: () => void;
  onAcceptWarnings: () => void;
  acceptPrefilledPending: boolean;
  acceptWarningsPending: boolean;
  onCommit: () => void;
  onReplaceFile: () => void;
}

function ProgressStepRow({
  label,
  state,
  detail,
}: {
  label: string;
  state: "done" | "current" | "upcoming";
  detail?: string | null;
}) {
  return (
    <li
      className={cn(
        "flex items-start gap-2.5 rounded-md px-2 py-1.5 text-sm",
        state === "current" && "bg-muted/50",
      )}
    >
      {state === "done" ? (
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
      ) : state === "current" ? (
        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
      ) : (
        <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden />
      )}
      <div className="min-w-0">
        <span
          className={cn(
            state === "done" && "text-muted-foreground",
            state === "current" && "font-medium text-foreground",
            state === "upcoming" && "text-muted-foreground/70",
          )}
        >
          {label}
        </span>
        {state === "current" && detail ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
        ) : null}
      </div>
    </li>
  );
}

function getCommitHintKey(
  phase: ImportSummaryPhase,
  preview: CreativeImportPreviewDto,
): string | null {
  if (phase === "ready") {
    return preview.committingRowCount > 0 ? null : "marketplace.admin.import.summary.noCommitRows";
  }
  switch (phase) {
    case "vendors":
    case "vendors-table":
      return "marketplace.admin.import.summary.commitHint.vendors";
    case "dates":
      return "marketplace.admin.import.summary.commitHint.dates";
    case "blockers":
      return "marketplace.admin.import.summary.commitHint.blockers";
    case "warnings":
      return "marketplace.admin.import.summary.commitHint.warnings";
    default:
      return "marketplace.admin.import.summary.completeChecklist";
  }
}

export function ImportSummaryPanel({
  preview,
  readiness,
  blockingIssues,
  warningIssues,
  hasVendorMapping,
  isCommitting,
  onOpenVendorMapping,
  onAcceptPrefilledDates,
  onAcceptWarnings,
  acceptPrefilledPending,
  acceptWarningsPending,
  onCommit,
  onReplaceFile,
}: ImportSummaryPanelProps) {
  const { t } = useTranslation();
  const [blockersOpen, setBlockersOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);

  useEffect(() => {
    if (readiness.warningsAcknowledged && warningsOpen) {
      setWarningsOpen(false);
    }
  }, [readiness.warningsAcknowledged, warningsOpen]);

  const blockingIssueCount = readiness.blockingIssueCount;
  const phase = deriveImportSummaryPhase(readiness, hasVendorMapping, blockingIssueCount);
  const stepNumber = progressStepNumber(phase);
  const unmappedVendorCount =
    readiness.unmappedVendorLabels + readiness.unmappedDirectRows;
  const commitHintKey = getCommitHintKey(phase, preview);

  const progressSteps: { id: ProgressStepId; labelKey: string; detail?: string | null }[] = [
    {
      id: "vendors",
      labelKey: "marketplace.admin.import.summary.progress.vendors",
      detail:
        phase === "vendors" || phase === "vendors-table"
          ? phase === "vendors-table"
            ? t("marketplace.admin.import.summary.nextStep.vendorsTable.detail", {
                count: readiness.unmappedDirectRows,
              })
            : t("marketplace.admin.import.summary.nextStep.vendors.detail", {
                count: unmappedVendorCount,
              })
          : null,
    },
    {
      id: "dates",
      labelKey: "marketplace.admin.import.summary.progress.dates",
      detail:
        phase === "dates"
          ? t("marketplace.admin.import.summary.nextStep.dates.detail", {
              count: readiness.rowsWithPrefilledDates,
            })
          : null,
    },
    {
      id: "issues",
      labelKey: "marketplace.admin.import.summary.progress.issues",
      detail:
        phase === "blockers"
          ? t("marketplace.admin.import.summary.nextStep.blockers.detail", {
              count: blockingIssueCount,
            })
          : phase === "warnings"
            ? t("marketplace.admin.import.summary.nextStep.warnings.detail", {
                count: readiness.warningIssueCount,
              })
            : null,
    },
  ];

  function renderNextStepActions() {
    switch (phase) {
      case "vendors":
        return (
          <Button type="button" className="w-full" onClick={onOpenVendorMapping}>
            {t("marketplace.admin.import.summary.nextStep.vendors.action")}
          </Button>
        );
      case "vendors-table":
        return null;
      case "dates":
        return (
          <Button
            type="button"
            className="w-full"
            disabled={acceptPrefilledPending}
            onClick={onAcceptPrefilledDates}
          >
            {acceptPrefilledPending
              ? t("marketplace.admin.import.quickActions.acceptingPrefilledDates")
              : t("marketplace.admin.import.summary.nextStep.dates.action")}
          </Button>
        );
      case "blockers":
        return (
          <Button
            type="button"
            variant="destructive"
            className="w-full"
            onClick={() => setBlockersOpen(true)}
          >
            {t("marketplace.admin.import.summary.nextStep.blockers.action", {
              count: blockingIssueCount,
            })}
          </Button>
        );
      case "warnings":
        return (
          <div className="flex flex-col gap-2">
            <Button type="button" className="w-full" onClick={() => setWarningsOpen(true)}>
              {t("marketplace.admin.import.summary.nextStep.warnings.reviewAction", {
                count: readiness.warningIssueCount,
              })}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={acceptWarningsPending}
              onClick={onAcceptWarnings}
            >
              {acceptWarningsPending
                ? t("marketplace.admin.import.quickActions.acceptingWarnings")
                : t("marketplace.admin.import.summary.nextStep.warnings.acknowledgeAction")}
            </Button>
          </div>
        );
      case "ready": {
        const frozenCount = preview.frozenRowCount ?? 0;
        return (
          <p className="text-sm text-muted-foreground">
            {frozenCount > 0
              ? t("marketplace.admin.import.summary.nextStep.ready.detailWithFrozen", {
                  count: preview.committingRowCount,
                  frozen: frozenCount,
                })
              : t("marketplace.admin.import.summary.nextStep.ready.detail", {
                  count: preview.committingRowCount,
                })}
          </p>
        );
      }
    }
  }

  return (
    <aside className="flex flex-col gap-4 lg:sticky lg:top-6 lg:self-start">
      <section className="rounded-xl border border-border/60 bg-card p-5">
        <h2 className="text-sm font-semibold">{t("marketplace.admin.import.summary.checklistTitle")}</h2>

        {phase !== "ready" ? (
          <div className="mt-4 rounded-lg border border-primary/25 bg-primary/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              {t("marketplace.admin.import.summary.nextStep.label", {
                current: stepNumber,
                total: 3,
              })}
            </p>
            <h3 className="mt-1.5 text-base font-semibold leading-snug">
              {t(`marketplace.admin.import.summary.nextStep.${phase}.title`)}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(`marketplace.admin.import.summary.nextStep.${phase}.description`, {
                count:
                  phase === "vendors" || phase === "vendors-table"
                    ? unmappedVendorCount
                    : phase === "dates"
                      ? readiness.rowsWithPrefilledDates
                      : phase === "blockers"
                        ? blockingIssueCount
                        : readiness.warningIssueCount,
              })}
            </p>
            <div className="mt-4">{renderNextStepActions()}</div>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-primary/25 bg-primary/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              {t("marketplace.admin.import.summary.nextStep.ready.label")}
            </p>
            <h3 className="mt-1.5 text-base font-semibold leading-snug">
              {t("marketplace.admin.import.summary.nextStep.ready.title")}
            </h3>
            {renderNextStepActions()}
          </div>
        )}

        <ol className="mt-4 space-y-0.5" aria-label={t("marketplace.admin.import.summary.progress.navLabel")}>
          {progressSteps.map((step) => (
            <ProgressStepRow
              key={step.id}
              label={t(step.labelKey)}
              state={progressStepState(step.id, readiness)}
              detail={step.detail}
            />
          ))}
        </ol>

        {phase !== "blockers" && blockingIssueCount > 0 ? (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-status-danger/30 bg-status-danger-subtle/40 px-3 py-2.5 text-sm text-status-danger">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p>{t("marketplace.admin.import.summary.blockersBanner", { count: blockingIssueCount })}</p>
              <button
                type="button"
                className="mt-1 text-xs font-medium underline underline-offset-2"
                onClick={() => setBlockersOpen(true)}
              >
                {t("marketplace.admin.import.summary.blockersBannerLink")}
              </button>
            </div>
          </div>
        ) : null}

        {readiness.warningsAcknowledged && warningIssues.length > 0 ? (
          <button
            type="button"
            className="mt-3 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => setWarningsOpen(true)}
          >
            {t("marketplace.admin.import.summary.viewWarningsLink", {
              count: warningIssues.length,
            })}
          </button>
        ) : null}

        {hasVendorMapping && readiness.steps.vendors && phase !== "vendors" ? (
          <button
            type="button"
            className="mt-3 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={onOpenVendorMapping}
          >
            {t("marketplace.admin.import.summary.reviewVendorMapping")}
          </button>
        ) : null}

        <div className="mt-6 space-y-2 border-t border-border/60 pt-5">
          <Button
            type="button"
            className="w-full"
            size="lg"
            disabled={!readiness.canCommit || isCommitting}
            onClick={onCommit}
          >
            {isCommitting
              ? t("marketplace.admin.import.committing")
              : t("marketplace.admin.import.commitAction")}
          </Button>
          {commitHintKey ? (
            <p className="text-center text-xs text-muted-foreground">
              {t(commitHintKey, {
                count:
                  phase === "blockers"
                    ? blockingIssueCount
                    : phase === "warnings"
                      ? readiness.warningIssueCount
                      : unmappedVendorCount,
              })}
            </p>
          ) : null}
        </div>
      </section>

      <section className="rounded-xl border border-border/60 bg-card p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted/50 text-muted-foreground">
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("marketplace.admin.import.summary.fileLabel")}
            </p>
            <p className="truncate text-sm font-medium">
              {preview.fileName ?? t("marketplace.admin.import.summary.unnamedFile")}
            </p>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3">
          {(
            [
              { key: "rows", label: t("marketplace.admin.import.stats.rows"), value: preview.rowCount },
              {
                key: "committing",
                label: t("marketplace.admin.import.stats.committing"),
                value: preview.committingRowCount,
              },
              {
                key: "frozen",
                label: t("marketplace.admin.import.stats.frozen"),
                value: preview.frozenRowCount ?? 0,
              },
              { key: "blocked", label: t("marketplace.admin.import.stats.blocked"), value: preview.blockedRowCount },
              {
                key: "blockingIssues",
                label: t("marketplace.admin.import.stats.blockingIssues"),
                value: readiness.blockingIssueCount,
              },
            ] as const
          )
            .filter((stat) => stat.key !== "frozen" || (preview.frozenRowCount ?? 0) > 0)
            .map((stat) => (
            <div key={stat.key} className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2">
              <dt className="text-[11px] text-muted-foreground">{stat.label}</dt>
              <dd className="text-lg font-semibold tabular-nums">{stat.value}</dd>
            </div>
          ))}
        </dl>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-3 h-auto w-full px-0 text-xs text-muted-foreground"
          onClick={onReplaceFile}
        >
          {t("marketplace.admin.import.summary.replaceFile")}
        </Button>
      </section>

      <ImportIssuesDialog
        open={blockersOpen}
        onOpenChange={setBlockersOpen}
        tone="blocking"
        issues={blockingIssues}
        rows={preview.rows}
      />
      <ImportIssuesDialog
        open={warningsOpen}
        onOpenChange={setWarningsOpen}
        tone="warning"
        issues={warningIssues}
        rows={preview.rows}
        acknowledged={readiness.warningsAcknowledged}
        onAcknowledge={readiness.warningsAcknowledged ? undefined : onAcceptWarnings}
        acknowledgePending={acceptWarningsPending}
      />
    </aside>
  );
}
